/**
 * Runtime configuration. The SPA is a static artifact shared across environments,
 * so environment-specific values (Cognito domain, client id, API base URL) are
 * loaded at runtime from `/config.json`, which CloudFormation/the pipeline writes
 * into the S3 bucket during deployment. This avoids baking secrets or
 * environment coupling into the built JavaScript.
 */

export interface RuntimeConfig {
  /** Cognito User Pool OIDC issuer, e.g. https://cognito-idp.eu-west-3.amazonaws.com/<poolId> */
  cognitoAuthority: string;
  /** App client id for the SPA (public client, PKCE). */
  cognitoClientId: string;
  /** Hosted UI domain, e.g. https://benilar-proserv.auth.eu-west-3.amazoncognito.com */
  cognitoDomain: string;
  /** Where the hosted UI redirects back to after login. */
  redirectUri: string;
  /** Post-logout redirect. */
  postLogoutRedirectUri: string;
  /** Base URL of the API behind the ALB. */
  apiBaseUrl: string;
  /** The Google Workspace IdP identifier configured on the User Pool. */
  identityProvider: string;
}

let cached: RuntimeConfig | undefined;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) {
    return cached;
  }
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load runtime config: ${res.status}`);
  }
  cached = (await res.json()) as RuntimeConfig;
  return cached;
}
